import { useState } from 'react';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import Pill from '../ui/Pill';
import { ErrorNotice, EmptyState } from '../ui/States';
import {
  Calibration,
  Decision,
  Deliberation,
  DeliberationRow,
  Equity,
  OUTCOME_LABELS,
  Outcome,
  Summary,
  errorMessage,
  showValue,
} from '../../services/reviewApi';
import { HeadRow, Td, Th, inputClass } from './parts';

const RATING_SHORT: Record<string, string> = { low: 'L', medium: 'M', high: 'H' };

export function SummaryCell({ summary }: { summary?: Summary }) {
  if (!summary) return <span className="text-ink-2">sealed</span>;
  return (
    <span className="tabular">
      {summary.meanPriority ?? '—'}
      <span className="text-ink-2"> · {summary.priorities.join(' / ') || 'none yet'}</span>
      {summary.flagged ? (
        <span className="ml-2">
          <Pill tone="failed">{`spread ${summary.spread}`}</Pill>
        </span>
      ) : null}
    </span>
  );
}

export function CalibrationTable({
  data,
  onReopen,
  onAddThird,
  onOpen,
}: {
  data: Calibration;
  onReopen?: (reviewId: string, reason: string) => Promise<void>;
  onAddThird?: (applicationId: string) => void;
  onOpen?: (applicationId: string) => void;
}) {
  const [reopening, setReopening] = useState<{ id: string; who: string | null } | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const scored = data.criteria.filter((c) => c.scale !== 'text' && c.key !== 'priority');
  const rows = data.rows.filter((r) => !r.recused);

  return (
    <div>
      <p className="text-small text-ink-2 mb-3">
        Priority scores that differ by {data.threshold} or more are flagged. Ratings are shown as L/M/H in rubric order.
        {data.recused ? ` ${data.recused} application(s) hidden: you have a conflict with them.` : ''}
      </p>
      {!rows.length ? (
        <EmptyState title="Nothing to calibrate yet">No eligible applications have reviews.</EmptyState>
      ) : (
        <table className="w-full">
          <thead>
            <HeadRow>
              <Th>Applicant</Th>
              <Th>Career stage</Th>
              <Th>Priority (mean · each)</Th>
              <Th>Reviews</Th>
              <Th />
            </HeadRow>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className={`border-b border-line ${r.summary?.flagged ? 'bg-warning-tint' : ''}`}>
                <Td>
                  {onOpen ? (
                    <button type="button" className="link-underline" onClick={() => onOpen(r.id)}>
                      {r.name}
                    </button>
                  ) : (
                    r.name
                  )}
                  <div className="text-small text-ink-2">{[r.discipline, r.region].filter(Boolean).join(' · ')}</div>
                </Td>
                <Td>{r.careerStage}</Td>
                <Td>
                  <SummaryCell summary={r.summary} />
                </Td>
                <Td>
                  {(r.reviews ?? []).map((rv) => (
                    <div key={rv.id} className="text-small mb-1">
                      <span className="font-semibold">{rv.reviewer}</span>{' '}
                      <span className="text-ink-2">({rv.state})</span>{' '}
                      {rv.scores ? (
                        <span className="tabular">
                          {rv.scores.priority?.score ?? '—'} ·{' '}
                          {scored.map((c) => RATING_SHORT[rv.scores?.[c.key]?.rating ?? ''] ?? '–').join('')}
                        </span>
                      ) : null}
                      {rv.canReopen && onReopen ? (
                        <button
                          type="button"
                          className="link-underline ml-2 text-small"
                          onClick={() => {
                            setReopening({ id: rv.id, who: rv.reviewer });
                            setReason('');
                            setError(null);
                          }}
                        >
                          Reopen
                        </button>
                      ) : null}
                    </div>
                  ))}
                  {r.hasThird ? <Pill tone="running">third review</Pill> : null}
                </Td>
                <Td>
                  {onAddThird && r.summary?.flagged && !r.hasThird ? (
                    <Button size="sm" variant="outline" onClick={() => onAddThird(r.id)}>
                      Add third reviewer
                    </Button>
                  ) : null}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {reopening && onReopen ? (
        <Modal
          title="Reopen review"
          onClose={() => setReopening(null)}
          footer={
            <div className="flex justify-end gap-3 w-full">
              <Button variant="ghost" onClick={() => setReopening(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={!reason.trim()}
                onClick={async () => {
                  try {
                    await onReopen(reopening.id, reason);
                    setReopening(null);
                  } catch (err) {
                    setError(errorMessage(err, 'Could not reopen the review.'));
                  }
                }}
              >
                Reopen
              </Button>
            </div>
          }
        >
          <p className="text-table mb-3">
            {reopening.who} will be able to change their scores and must resubmit with a reason. Only they can change
            them.
          </p>
          <label className="block text-small text-ink-2 mb-1">Reason (recorded)</label>
          <textarea className={`${inputClass} h-[80px]`} value={reason} onChange={(e) => setReason(e.target.value)} />
          {error ? (
            <div className="mt-3">
              <ErrorNotice>{error}</ErrorNotice>
            </div>
          ) : null}
        </Modal>
      ) : null}
    </div>
  );
}

export function EquitySummary({ equity }: { equity: Equity }) {
  return (
    <div>
      <p className="text-small text-ink-2 mb-3">
        {equity.applicants} eligible applicants, {equity.accepted} accepted or conditional so far. This view stays
        inside the review module.
      </p>
      <div className="grid grid-cols-2 gap-x-8 gap-y-4">
        {equity.dimensions.map((d) => (
          <div key={d.key}>
            <div className="font-semibold text-small mb-1">{d.label}</div>
            <table className="w-full">
              <tbody>
                {d.values.map((v) => (
                  <tr key={v.value} className="border-b border-line">
                    <td className="py-[5px] text-small">{v.value}</td>
                    <td className="py-[5px] text-small tabular text-right">{v.applicants}</td>
                    <td className="py-[5px] text-small tabular text-right text-ink-2 w-24">{v.accepted} accepted</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}

export function DecisionModal({
  row,
  onClose,
  onSave,
}: {
  row: DeliberationRow;
  onClose: () => void;
  onSave: (body: Record<string, string>) => Promise<void>;
}) {
  const current = row.decision;
  const [outcome, setOutcome] = useState<Outcome>(current?.outcome ?? 'accepted');
  const [rationale, setRationale] = useState('');
  const [conditionText, setConditionText] = useState(current?.conditionText ?? '');
  const [conditionDueDate, setConditionDueDate] = useState(current?.conditionDueDate ?? '');
  const [conditionOwner, setConditionOwner] = useState(current?.conditionOwner ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const conditional = outcome === 'conditional';
  const ready =
    (!current || rationale.trim()) &&
    (!conditional || (conditionText.trim() && conditionDueDate && conditionOwner.trim()));

  return (
    <Modal
      title={current ? 'Change decision' : 'Record decision'}
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-3 w-full">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!ready || saving}
            onClick={async () => {
              setSaving(true);
              try {
                await onSave({ outcome, rationale, conditionText, conditionDueDate, conditionOwner });
                onClose();
              } catch (err) {
                setError(errorMessage(err, 'Could not record the decision.'));
              } finally {
                setSaving(false);
              }
            }}
          >
            {saving ? 'Saving…' : 'Record'}
          </Button>
        </div>
      }
    >
      <p className="text-table mb-3">
        <span className="font-semibold">{row.name}</span> · {row.careerStage} · mean priority{' '}
        {row.summary.meanPriority ?? '—'}
      </p>
      {current ? (
        <p className="text-small text-ink-2 mb-3">
          Current decision: {OUTCOME_LABELS[current.outcome]}. A change is recorded as an amendment; the original stays.
        </p>
      ) : null}
      <label className="block text-small text-ink-2 mb-1">Outcome</label>
      <select className={inputClass} value={outcome} onChange={(e) => setOutcome(e.target.value as Outcome)}>
        {(['accepted', 'transferred', 'conditional', 'ineligible'] as Outcome[]).map((o) => (
          <option key={o} value={o}>
            {OUTCOME_LABELS[o]}
          </option>
        ))}
      </select>
      {conditional ? (
        <>
          <label className="block text-small text-ink-2 mt-3 mb-1">Condition</label>
          <textarea
            className={`${inputClass} h-[64px]`}
            value={conditionText}
            onChange={(e) => setConditionText(e.target.value)}
          />
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <label className="block text-small text-ink-2 mb-1">Due date</label>
              <input
                type="date"
                className={inputClass}
                value={conditionDueDate}
                onChange={(e) => setConditionDueDate(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-small text-ink-2 mb-1">Who checks it</label>
              <input
                className={inputClass}
                value={conditionOwner}
                onChange={(e) => setConditionOwner(e.target.value)}
              />
            </div>
          </div>
        </>
      ) : null}
      <label className="block text-small text-ink-2 mt-3 mb-1">
        Rationale{current ? ' (required for a change)' : ''}
      </label>
      <textarea className={`${inputClass} h-[72px]`} value={rationale} onChange={(e) => setRationale(e.target.value)} />
      {outcome === 'accepted' ? (
        <p className="text-small text-ink-2 mt-2">
          Accepted adds the applicant to the Fellow group and this year’s Fellow group.
        </p>
      ) : null}
      {error ? (
        <div className="mt-3">
          <ErrorNotice>{error}</ErrorNotice>
        </div>
      ) : null}
    </Modal>
  );
}

export function DecisionCell({ decision }: { decision?: Decision | null }) {
  if (decision === undefined) return <span className="text-ink-2">—</span>;
  if (!decision) return <span className="text-ink-2">not decided</span>;
  return (
    <div>
      <Pill
        tone={decision.outcome === 'accepted' ? 'healthy' : decision.outcome === 'conditional' ? 'running' : 'inactive'}
      >
        {OUTCOME_LABELS[decision.outcome]}
      </Pill>
      {decision.conditionText ? (
        <div className="text-small text-ink-2 mt-1">
          {decision.conditionText} · due {decision.conditionDueDate} · {decision.conditionOwner} ·{' '}
          {showValue(decision.conditionStatus)}
        </div>
      ) : null}
      {decision.groupSync?.some((g) => g.status !== 'added' && g.status !== 'already in the group') ? (
        <div className="text-small text-warning mt-1">
          Groups: {decision.groupSync.map((g) => `${g.group ?? ''} ${g.status}`).join('; ')}
        </div>
      ) : null}
    </div>
  );
}

export function DeliberationTable({
  data,
  onDecide,
  onOpen,
  onCondition,
}: {
  data: Deliberation;
  onDecide?: (row: DeliberationRow, body: Record<string, string>) => Promise<void>;
  onOpen?: (applicationId: string) => void;
  onCondition?: (decisionId: string, status: string) => Promise<void>;
}) {
  const [deciding, setDeciding] = useState<DeliberationRow | null>(null);
  const showSelfId = data.perApplicantSelfId;
  const showRecusals = data.rows.some((r) => r.recusals);

  return (
    <div>
      <p className="text-small text-ink-2 mb-3">
        Ranked by mean priority score; rank within career stage alongside. Self-identification is never part of the
        ranking.
        {data.recused ? ` ${data.recused} application(s) hidden: you have a conflict with them.` : ''}
      </p>
      {!data.rows.length ? (
        <EmptyState title="No applications to discuss">Nothing has reached deliberation.</EmptyState>
      ) : (
        <table className="w-full">
          <thead>
            <HeadRow>
              <Th>Rank</Th>
              <Th>Applicant</Th>
              <Th>Career stage</Th>
              <Th>Priority</Th>
              {showSelfId ? <Th>Self-ID</Th> : null}
              {showRecusals ? <Th>Recused</Th> : null}
              <Th>Decision</Th>
              <Th />
            </HeadRow>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.id} className="border-b border-line">
                <Td className="tabular">{r.rank ?? '—'}</Td>
                <Td>
                  {onOpen ? (
                    <button type="button" className="link-underline" onClick={() => onOpen(r.id)}>
                      {r.name}
                    </button>
                  ) : (
                    r.name
                  )}
                  <div className="text-small text-ink-2">
                    {[r.institution, r.discipline, r.region].filter(Boolean).join(' · ')}
                  </div>
                </Td>
                <Td>
                  {r.careerStage}
                  <span className="text-ink-2 tabular"> #{r.stageRank ?? '—'}</span>
                </Td>
                <Td>
                  <SummaryCell summary={r.summary} />
                </Td>
                {showSelfId ? (
                  <Td>
                    {(r.selfId ?? [])
                      .filter((s) => s.value !== null && s.value !== undefined && s.value !== '')
                      .map((s) => (
                        <div key={s.key} className="text-small">
                          <span className="text-ink-2">{s.label}:</span> {showValue(s.value)}
                        </div>
                      ))}
                  </Td>
                ) : null}
                {showRecusals ? <Td className="text-small">{(r.recusals ?? []).join(', ') || '—'}</Td> : null}
                <Td>
                  <DecisionCell decision={r.decision} />
                  {onCondition && r.decision?.outcome === 'conditional' && r.decision.conditionStatus === 'pending' ? (
                    <div className="mt-1 flex gap-2">
                      <button
                        type="button"
                        className="link-underline text-small"
                        onClick={() => onCondition(r.decision!.id, 'met')}
                      >
                        Condition met
                      </button>
                      <button
                        type="button"
                        className="link-underline text-small"
                        onClick={() => onCondition(r.decision!.id, 'not_met')}
                      >
                        Not met
                      </button>
                    </div>
                  ) : null}
                </Td>
                <Td>
                  {r.canDecide && onDecide ? (
                    <Button size="sm" variant={r.decision ? 'ghost' : 'outline'} onClick={() => setDeciding(r)}>
                      {r.decision ? 'Change' : 'Decide'}
                    </Button>
                  ) : null}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {deciding && onDecide ? (
        <DecisionModal row={deciding} onClose={() => setDeciding(null)} onSave={(body) => onDecide(deciding, body)} />
      ) : null}
    </div>
  );
}

export function ExportModal({
  onClose,
  onExport,
  allowSelfId,
}: {
  onClose: () => void;
  onExport: (body: Record<string, unknown>) => Promise<void>;
  allowSelfId: boolean;
}) {
  const [reason, setReason] = useState('');
  const [includeSelfId, setIncludeSelfId] = useState(false);
  const [includeNotes, setIncludeNotes] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <Modal
      title="Export"
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-3 w-full">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!reason.trim()}
            onClick={async () => {
              try {
                await onExport({ reason, includeSelfId, includeNotes });
                onClose();
              } catch (err) {
                setError(errorMessage(err, 'Export failed.'));
              }
            }}
          >
            Download CSV
          </Button>
        </div>
      }
    >
      <p className="text-table mb-3">
        The file carries your name and the time, and the export is logged. Keep it off shared drives and delete it when
        the cycle is done.
      </p>
      <label className="block text-small text-ink-2 mb-1">Why are you exporting? (recorded)</label>
      <textarea className={`${inputClass} h-[64px]`} value={reason} onChange={(e) => setReason(e.target.value)} />
      <label className="flex items-center gap-2 text-table mt-3">
        <input type="checkbox" checked={includeNotes} onChange={(e) => setIncludeNotes(e.target.checked)} /> Include
        reviewer notes
      </label>
      {allowSelfId ? (
        <label className="flex items-center gap-2 text-table mt-2">
          <input type="checkbox" checked={includeSelfId} onChange={(e) => setIncludeSelfId(e.target.checked)} /> Include
          self-identification
        </label>
      ) : null}
      {error ? (
        <div className="mt-3">
          <ErrorNotice>{error}</ErrorNotice>
        </div>
      ) : null}
    </Modal>
  );
}
